import type { ChangeEvent } from "react";
import { useCallback, useEffect, useMemo } from "react";

import { skipToken } from "@reduxjs/toolkit/query";

import type { Product } from "@jti/magento";
import { getUser } from "@jti/rest-api";
import { type CampaignOrderData, type GetEligibleCampaignsResponseType } from "@jti/rest-api-types";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { CheckoutV2LoyaltyChoice, checkoutV2Slice } from "@jti/store/slices/checkoutV2Slice";
import {
  useApplyLoyaltyCampaignMutation,
  useGetLoyaltyPointsQuery,
  useGetLoyaltyVouchersQuery,
  useRemoveLoyaltyCampaignMutation,
} from "@jti/store/slices/loyaltyApiSlice";
import { useGetCustomerCartIdQuery } from "@jti/store/slices/magentoApiSlice";

import { useCartItems } from "@checkout-v2/shared/hooks/useCartItems";
import { useCartPrices } from "@checkout-v2/shared/hooks/useCartPrices";

import {
  buildLoyaltyCampaignOrderData,
  calculateLoyaltyCartPoints,
  filterEligibleLoyaltyVouchers,
  getMaximumLoyaltyVoucherValue,
  getMinimumLoyaltyVoucherCost,
  setLoyaltyCartPoints,
  updateLoyaltyUserSelection,
} from "../../utils";

const EMPTY_CART_ITEMS: Product[] = [];
const EMPTY_VOUCHERS: GetEligibleCampaignsResponseType[] = [];

export type UseLoyaltyCampaignManagementResult = {
  cartPoints: number;
  loyaltyChoice: CheckoutV2LoyaltyChoice | undefined;
  userPoints: number;
  vouchers: GetEligibleCampaignsResponseType[];
  isQueryCampaign: boolean;
  loyaltyCampaignId: string;
  isLoyaltyError: boolean;
  maxVoucherValue: number;
  minVoucherCost: number;
  isRedeemAvailable: boolean;
  setCollect: () => void;
  setRedeem: () => void;
  handleCampaignChange: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
};

export const useLoyaltyCampaignManagement = (): UseLoyaltyCampaignManagementResult => {
  const dispatch = useDispatch();
  const { loyaltyChoice, isLoyaltyError, loyaltyCampaignId } = useSelector(
    (store) => store.checkoutV2.loyaltyPointsState
  );

  const { data: cartItemsData } = useCartItems();
  const cartItems = cartItemsData ?? EMPTY_CART_ITEMS;
  const { data: cartPrices } = useCartPrices();
  const cartSubTotalIncludingTax = cartPrices?.subtotalIncludingTax?.value;
  const minimumPayableAmount = cartPrices?.minimumPayableAmount?.value;

  const cartPoints = useMemo(() => calculateLoyaltyCartPoints(cartItems), [cartItems]);

  const user = getUser();
  const uuid = user?.uuid;
  const ciam_token = user?.ciam_token;
  const { data: loyaltyPointsData } = useGetLoyaltyPointsQuery(uuid && ciam_token ? { uuid, ciam_token } : skipToken);
  const userPoints = loyaltyPointsData?.points ?? 0;

  const { data: cartIdData } = useGetCustomerCartIdQuery();
  const cartId = cartIdData?.customerCart?.id;
  const [applyCampaignMutation, { isLoading: isQueryCampaign }] = useApplyLoyaltyCampaignMutation();
  const [removeCampaignMutation] = useRemoveLoyaltyCampaignMutation();

  const campaignOrderData = useMemo<CampaignOrderData | undefined>(
    () =>
      buildLoyaltyCampaignOrderData({
        cartItems,
        cartSubTotalIncludingTax,
        minimumPayableAmount,
      }),
    [cartItems, cartSubTotalIncludingTax, minimumPayableAmount]
  );

  const { data: vouchersData } = useGetLoyaltyVouchersQuery(
    uuid && ciam_token && campaignOrderData ? { uuid, ciam_token, ...campaignOrderData } : skipToken
  );

  const vouchers = vouchersData ?? EMPTY_VOUCHERS;
  const availableVouchers = useMemo(() => filterEligibleLoyaltyVouchers(vouchers), [vouchers]);

  const maxVoucherValue = getMaximumLoyaltyVoucherValue(vouchers);
  const minVoucherCost = getMinimumLoyaltyVoucherCost(vouchers);
  const isRedeemAvailable = Boolean(cartId) && availableVouchers.length > 0 && userPoints >= minVoucherCost;

  const handleApplyCampaign = useCallback(
    async (campaignUuid: string) => {
      if (!cartId) {
        return;
      }

      try {
        await applyCampaignMutation({ cartId, campaign_uuid: campaignUuid }).unwrap();

        dispatch(checkoutV2Slice.actions.setLoyaltyCampaignId(campaignUuid));

        const activeVoucher = vouchers.find((voucher) => voucher.campaign_id === campaignUuid);
        if (activeVoucher) {
          dispatch(checkoutV2Slice.actions.setLoyaltyVoucher(activeVoucher));
        }
      } catch {
        dispatch(checkoutV2Slice.actions.setLoyaltyError(true));

        removeCampaignMutation({ cartId });
      }
    },
    [cartId, applyCampaignMutation, removeCampaignMutation, dispatch, vouchers]
  );

  const handleCampaignChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
      dispatch(checkoutV2Slice.actions.setLoyaltyError(false));

      await handleApplyCampaign(event.target.value);
    },
    [dispatch, handleApplyCampaign]
  );

  const setCollect = useCallback(() => {
    if (loyaltyChoice === CheckoutV2LoyaltyChoice.COLLECT) {
      return;
    }

    dispatch(checkoutV2Slice.actions.setLoyaltyChoice(CheckoutV2LoyaltyChoice.COLLECT));

    if (!cartId) {
      return;
    }

    updateLoyaltyUserSelection(CheckoutV2LoyaltyChoice.COLLECT, cartId);

    if (loyaltyCampaignId) {
      removeCampaignMutation({ cartId });
      dispatch(checkoutV2Slice.actions.setLoyaltyCampaignId(""));
    }
  }, [dispatch, cartId, loyaltyChoice, loyaltyCampaignId, removeCampaignMutation]);

  const setRedeem = useCallback(() => {
    if (!cartId) {
      return;
    }

    dispatch(checkoutV2Slice.actions.setLoyaltyChoice(CheckoutV2LoyaltyChoice.REDEEM));

    updateLoyaltyUserSelection(CheckoutV2LoyaltyChoice.REDEEM, cartId);
  }, [dispatch, cartId]);

  useEffect(() => {
    setLoyaltyCartPoints(cartItems, loyaltyChoice ?? CheckoutV2LoyaltyChoice.COLLECT, cartPoints);
    dispatch(checkoutV2Slice.actions.setLoyaltyPoints(cartPoints));
  }, [cartItems, loyaltyChoice, cartPoints, dispatch]);

  return {
    cartPoints,
    loyaltyChoice,
    loyaltyCampaignId,
    isLoyaltyError,
    userPoints,
    vouchers,
    maxVoucherValue,
    minVoucherCost,
    isQueryCampaign,
    isRedeemAvailable,
    setCollect,
    setRedeem,
    handleCampaignChange,
  };
};
